import React, { useState, useRef, useEffect } from "react";
import {
  GripVertical,
  X,
  Type,
  Hash,
  Image,
  Tag,
  List,
  BookOpen,
} from "lucide-react";
import { Input } from "@/components/ui/InputDefault";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/DefaultSelectDropdown";
import { FormField } from "../../types";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { useTagsDataContext } from "@/providers/TagsDataProvider";
import {
  AVAILABLE_FIELDS,
} from "@/lib/utils/fieldDefinitions";
import FormFieldButtons from "./FormFieldButtons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/user-feedback/tooltip";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import IconProvider from "@/components/ui/IconProvider";
import { TitleField } from "@/components/ui/text_fields/TitleField";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { RoleVisibilitySelector, getVisibilityState } from "@/components/ui/RoleVisibilitySelector";
// Get field types from fieldDefinitions
type FieldType = keyof typeof AVAILABLE_FIELDS;

interface FormFieldProps {
  field: FormField;
  sectionId: string;
  onUpdate: (
    sectionId: string,
    fieldId: string,
    updates: Partial<FormField>
  ) => void;
  onRemove: (sectionId: string, fieldId: string) => void;
  isNewField?: boolean;
  onClearNewField?: () => void;
  disabled?: boolean;
  showDocumentation?: boolean;
  previewMode?: boolean;
  previewRole?: string | null;
  onFieldVisibilityChange?: (sectionId: string, fieldId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
}

export default function FormFieldMainComponent({
  field,
  sectionId,
  onUpdate,
  onRemove,
  isNewField,
  onClearNewField,
  disabled = false,
  showDocumentation = false,
  previewMode = false,
  previewRole = null,
  onFieldVisibilityChange,
}: FormFieldProps) {
  const { t } = usePlatformTranslation();
  const [labelValue, setLabelValue] = useState(field.label);
  const [showDocEditor, setShowDocEditor] = useState(false);
  const { types, isLoading: typesLoading } = useTypesDataContext();
  const { tags, isLoading: tagsLoading, refreshTags } = useTagsDataContext();
  const labelInputRef = useRef<HTMLInputElement>(null);
  
  // Helper function to get filter option names
  const getFilterOptionNames = (filterOptions: string[] | undefined, fieldType: string): string[] => {
    if (!filterOptions || filterOptions.length === 0) return [];
    
    if (fieldType === 'types') {
      return filterOptions
        .map(id => types.find(t => t.id === id)?.name)
        .filter((name): name is string => !!name);
    } else if (fieldType === 'glossaryItem') {
      // For glossary items, we'd need to access the objectsStore
      // For now, just show the count since we don't have easy access to names in view mode
      return filterOptions;
    }
    return [];
  };

  // Autofocus on newly created fields
  useEffect(() => {
    if (isNewField && labelInputRef.current) {
      // Use setTimeout to ensure the component is fully rendered
      setTimeout(() => {
        labelInputRef.current?.focus();
        labelInputRef.current?.select(); // Select all text for easy replacement
        // Clear the new field state after focusing
        onClearNewField?.();
      }, 100);
    }
  }, [isNewField, onClearNewField]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  // Handle label change (update local state only)
  const handleLabelChange = (value: string) => {
    setLabelValue(value);
  };

  // Save label changes when focus is lost
  const handleLabelBlur = () => {
    if (labelValue !== field.label) {
      onUpdate(sectionId, field.id, { label: labelValue });
    }
  };

  // Handle documentation changes
  const handleDocumentationChange = (value: string) => {
    onUpdate(sectionId, field.id, { documentation: value });
  };

  // Handle type selection for types field
/*   const handleTypeToggle = (typeId: string) => {
    alert("typeId");
    console.log("typeId", typeId);
    const currentSelectedTypes = field.selectedTypes || [];
    const newSelectedTypes = currentSelectedTypes.includes(typeId)
      ? currentSelectedTypes.filter((id) => id !== typeId)
      : [...currentSelectedTypes, typeId];

    onUpdate(sectionId, field.id, { selectedTypes: newSelectedTypes });
  }; */

  // Get available field types from fieldDefinitions
  const availableFieldTypes = Object.keys(AVAILABLE_FIELDS) as FieldType[];

  // Get field type label for display
  const fieldTypeDef = AVAILABLE_FIELDS[field.type as FieldType];
  const fieldTypeLabel = fieldTypeDef?.label || field.type;

  // Check if field is readonly (for preview mode)
  const isReadOnly = (field as any).isReadOnly || false;
  
  // Render view mode display
  if (disabled) {
    const filterNames = getFilterOptionNames(field.filterOptions, field.type);
    
    return (
      <>
        <div className={`p-2 border border-border rounded-sm ${
          isReadOnly ? 'bg-muted opacity-70' : 'bg-card'
        }`}>
          {/* Main field info with icons on same line */}
          <div className="flex items-center gap-2 justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`font-medium ${isReadOnly ? 'text-muted-foreground' : 'text-foreground'}`}>
                {field.label}
              </div>
              <span className="text-sm text-muted-foreground">• {fieldTypeLabel}</span>
            </div>
            
            {/* Field setting icons - matching edit mode - aligned to right */}
            <div className="flex items-center gap-1 ml-auto">
              {/* Mandatory Icon */}
              {field.mandatory && (
                <TooltipProvider delayDuration={100} skipDelayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-6 h-6 flex items-center justify-center rounded bg-purple-1/20 text-purple-1">
                        <IconProvider iconstring="fa-asterisk" size={12} />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Required field</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              {/* Multiple Icon */}
              {field.multiple && (
                <TooltipProvider delayDuration={100} skipDelayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-6 h-6 flex items-center justify-center rounded bg-blue-500/20 text-blue-600 dark:text-blue-400">
                        <IconProvider iconstring="fa-list" size={12} />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Multiple values allowed</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              {/* Filter Icon with count */}
              {field.filter && field.filterOptions && field.filterOptions.length > 0 && (
                <TooltipProvider delayDuration={100} skipDelayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative w-6 h-6 flex items-center justify-center rounded bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                        <IconProvider iconstring="fa-filter" size={12} />
                        <span className="absolute -top-1 -right-1 text-[10px] bg-indigo-500/30 text-indigo-700 dark:text-indigo-300 px-1 py-0.5 rounded-full min-w-[16px] h-[16px] flex items-center justify-center font-medium">
                          {field.filterOptions.length}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div>
                        Filtered ({field.filterOptions.length} {field.type === 'types' ? 'types' : 'items'})
                        {filterNames.length > 0 && filterNames.length <= 3 && (
                          <div className="text-xs opacity-75 mt-1">{filterNames.join(', ')}</div>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              {/* Translation Icon */}
              {field.translation && (
                <TooltipProvider delayDuration={100} skipDelayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-6 h-6 flex items-center justify-center rounded bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                        <IconProvider iconstring="fa-globe" size={12} />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Translatable field</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              {/* Read-Only Badge (when in preview mode) */}
              {isReadOnly && (
                <TooltipProvider delayDuration={100} skipDelayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400">
                        Read-Only
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>This field is read-only for this role</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              {/* Role Visibility Selector - Show when a role is selected */}
              {previewRole && onFieldVisibilityChange && (
                <RoleVisibilitySelector
                  state={getVisibilityState(
                    previewRole,
                    field.hiddenForRoles,
                    field.readOnlyForRoles
                  )}
                  onStateChange={(newState) => {
                    onFieldVisibilityChange(sectionId, field.id, previewRole, newState);
                  }}
                  disabled={false}
                  size="sm"
                />
              )}
            </div>
          </div>
        </div>
        {/* Documentation Display - When toggle is on */}
        {showDocumentation && (
          <div className={`mt-2 ml-6 p-2 rounded text-sm ${field.documentation ? 'bg-blue-500/10 border-l-4 border-blue-500 dark:bg-blue-500/20' : 'bg-muted border-l-4 border-border'}`}>
            <div className="flex items-start gap-2">
              <IconProvider 
                iconstring={field.documentation ? "fa-circle-info" : "fa-circle-exclamation"} 
                size={12} 
                className={`mt-0.5 ${field.documentation ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}`} 
              />
              {field.documentation ? (
                <div 
                  className="text-foreground flex-1"
                  dangerouslySetInnerHTML={{ __html: field.documentation }}
                />
              ) : (
                <div className="text-muted-foreground italic flex-1">
                  No documentation available
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  // Render edit mode
  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-2 p-2 border border-border rounded-sm bg-card transition-colors"
      >
        {/* Drag Handle */}
        <div 
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="h-4 w-4" />
        </div>



        {/* Field Label Input */}
        <div className="flex-1 relative">
          <TitleField
            value={labelValue}
            placeholder={t('platform.wizards.blueprints.field_label')}
            required={field.mandatory}
            width={"full"}
            onChange={handleLabelChange}
            onBlur={handleLabelBlur}
          />
          {field.mandatory && (
            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#6E35FF] font-medium">
              *
            </span>
          )}
        </div>

        {/* Field Type Dropdown - Disable when filter options are present */}
        {field.filterOptions && field.filterOptions.length > 0 ? (
          <TooltipProvider delayDuration={100} skipDelayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-32 h-8 min-w-32 opacity-50 cursor-not-allowed bg-muted flex items-center justify-between px-3 py-2 text-sm rounded border border-border text-foreground">
                  <span className="truncate">{fieldTypeLabel}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('platform.wizards.blueprints.remove_filter_options_first')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Select
            value={field.type}
            onValueChange={(value: FieldType) => {
              console.log('[FormFieldMain] Type changed to:', value);
              onUpdate(sectionId, field.id, { type: value })
            }}
          >
            <SelectTrigger className="w-32 h-8 min-w-32">
              <SelectValue>
                <span className="truncate">{fieldTypeLabel}</span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {availableFieldTypes.map((fieldType) => {
                const fieldDef = AVAILABLE_FIELDS[fieldType];
                return (
                  <SelectItem key={fieldType} value={fieldType}>
                    {fieldDef.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}

        {/* Button Container */}
        <FormFieldButtons
          field={field}
          sectionId={sectionId}
          onUpdate={onUpdate}
          onRemove={onRemove}
          showDocEditor={showDocEditor}
          onToggleDocEditor={() => setShowDocEditor(!showDocEditor)}
        />
      </div>

      {/* Documentation Editor */}
      {showDocEditor && (
        <div className="ml-6 mt-2">
          <textarea
            dir="auto"
            className="w-full p-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            style={{ minHeight: '50px', maxHeight: '150px' }}
            placeholder={t('platform.wizards.blueprints.add_documentation_for_this_field')}
            defaultValue={field.documentation || ''}
            onChange={(e) => handleDocumentationChange(e.target.value)}
          />
        </div>
      )}

      {/* Documentation Display - When toggle is on */}
      {showDocumentation && !showDocEditor && (
        <div className={`ml-6 mt-2 p-2 rounded text-sm ${field.documentation ? 'bg-blue-500/10 border-l-4 border-blue-500 dark:bg-blue-500/20' : 'bg-muted border-l-4 border-border'}`}>
          <div className="flex items-start gap-2">
            <IconProvider 
              iconstring={field.documentation ? "fa-circle-info" : "fa-circle-exclamation"} 
              size={12} 
              className={`mt-0.5 ${field.documentation ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}`} 
            />
            {field.documentation ? (
              <div 
                className="text-foreground flex-1"
                dangerouslySetInnerHTML={{ __html: field.documentation }}
              />
            ) : (
              <div className="text-muted-foreground italic flex-1">
                No documentation available
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
